import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { addMentor, getAdminMentors, promoteMentor, setCareerStrategist, setPlacementCoordinator, updateMentorDetails } from '../../lib/api';

interface MentorRow {
  mentorId: string;
  mentorName: string;
  passcode: string;
  status?: string;
  phone?: string;
  role?: string;
  seniorMentorId?: string;
  isPlacementCoordinator?: boolean;
  // A Career Strategist takes no classes: they run the P2P students after the test fee
  isCareerStrategist?: boolean;
  email?: string;
  joiningDate?: string;
  birthday?: string;
}

/* ── Animations ──────────────────────────────────────────────────────────── */
const ANIM_CSS = `
@keyframes mmFadeUp   { from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);} }
@keyframes mmPop      { 0%{transform:scale(.88);opacity:0;}60%{transform:scale(1.05);}100%{transform:scale(1);opacity:1;} }
@keyframes mmSlideIn  { from{opacity:0;transform:translateX(-16px);}to{opacity:1;transform:translateX(0);} }
@keyframes mmShimmer  { 0%{background-position:200% 0;}100%{background-position:-200% 0;} }
@keyframes mmPulse    { 0%,100%{transform:scale(1);opacity:1;}50%{transform:scale(1.5);opacity:.3;} }
@keyframes mmGlow     { 0%,100%{box-shadow:0 0 0 0 rgba(167,139,250,.5);}50%{box-shadow:0 0 0 10px rgba(167,139,250,0);} }
@keyframes mmBdayGlow { 0%,100%{box-shadow:0 0 0 0 rgba(252,165,165,.6);}50%{box-shadow:0 0 0 14px rgba(252,165,165,0);} }
@keyframes mmBounce   { 0%,100%{transform:translateY(0) scale(1);}30%{transform:translateY(-10px) scale(1.05);}60%{transform:translateY(-3px) scale(1);} }
@keyframes mmFloat    { 0%,100%{transform:translateY(0);}50%{transform:translateY(-5px);} }
@keyframes mmSpin     { from{transform:rotate(0deg);}to{transform:rotate(360deg);} }
@keyframes mmBannerIn { from{opacity:0;transform:translateY(-12px);}to{opacity:1;transform:translateY(0);} }
@keyframes mmFirePulse{ 0%,100%{text-shadow:0 0 8px #ff6535,0 0 16px #ff6535;}50%{text-shadow:0 0 20px #ff0,0 0 40px #ff6535;} }
@keyframes mmRingRotate { from{transform:rotate(0deg);}to{transform:rotate(360deg);} }
@keyframes mmCountUp  { from{opacity:0;transform:scale(.7);}to{opacity:1;transform:scale(1);} }
@keyframes mmStarSpin { from{transform:rotate(0deg) scale(1);}50%{transform:rotate(180deg) scale(1.2);}to{transform:rotate(360deg) scale(1);} }

.mm-card       { animation: mmFadeUp .4s cubic-bezier(.22,1,.36,1) both; }
.mm-pop        { animation: mmPop .32s cubic-bezier(.34,1.56,.64,1) both; }
.mm-slide      { animation: mmSlideIn .35s ease both; }
.mm-banner     { animation: mmBannerIn .4s cubic-bezier(.22,1,.36,1) both; }
.mm-float      { animation: mmFloat 3s ease-in-out infinite; }
.mm-bounce     { animation: mmBounce 1.4s ease-in-out infinite; }
.mm-fire       { animation: mmFirePulse 1.2s ease-in-out infinite; }
.mm-star       { animation: mmStarSpin 3s linear infinite; }
.mm-glow       { animation: mmGlow 2s ease-in-out infinite; }
.mm-bday-glow  { animation: mmBdayGlow 1.5s ease-in-out infinite; }
.mm-pulse-dot::after {
  content:''; position:absolute; inset:0; border-radius:9999px;
  animation: mmPulse 1.6s ease-in-out infinite;
}
.mm-shimmer {
  background: linear-gradient(90deg,#fde68a 0%,#fbbf24 40%,#fde68a 100%);
  background-size: 200% 100%;
  animation: mmShimmer 2.4s linear infinite;
}
.mm-card:hover { transform: translateY(-3px); box-shadow: 0 16px 40px rgba(49,39,65,.12); }
.mm-card { transition: transform .25s ease, box-shadow .25s ease; }
`;

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function daysSince(dateStr: string): number {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 0;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function tenureLabel(days: number): string {
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  const yrs = Math.floor(days / 365);
  const mo  = Math.floor((days % 365) / 30);
  return mo > 0 ? `${yrs}yr ${mo}mo` : `${yrs}yr`;
}

function birthdayCountdown(birthday: string): number | null {
  if (!birthday) return null;
  const [mm, dd] = birthday.split('-').map(Number);
  if (!mm || !dd) return null;
  const now  = new Date();
  const next = new Date(now.getFullYear(), mm - 1, dd);
  if (next < now) next.setFullYear(now.getFullYear() + 1);
  return Math.ceil((next.getTime() - now.getTime()) / 86400000);
}

function roleGradient(role?: string) {
  if (role === 'Senior')              return 'linear-gradient(135deg,#b45309,#f59e0b)';
  if (role === 'Student Coordinator') return 'linear-gradient(135deg,#1d4ed8,#3b82f6)';
  return 'linear-gradient(135deg,#312741,#6d28d9)';
}

function roleAccentColor(role?: string) {
  if (role === 'Senior')              return '#f59e0b';
  if (role === 'Student Coordinator') return '#3b82f6';
  return '#a78bfa';
}

function roleBg(role?: string) {
  if (role === 'Senior')              return { bg: '#fef3c7', color: '#92400e', border: '#fde68a' };
  if (role === 'Student Coordinator') return { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
  return { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd' };
}

function roleLabel(role?: string) {
  if (role === 'Senior')              return '⭐ Senior Mentor';
  if (role === 'Student Coordinator') return '🎓 Student Coord.';
  return '👨‍💻 Mentor';
}

/* ── MentorCard ──────────────────────────────────────────────────────────── */
interface CardProps {
  m: MentorRow;
  idx: number;
  showPasscodes: boolean;
  allMentors: MentorRow[];
  onEdit: (m: MentorRow) => void;
  onPromote: (m: MentorRow) => void;
  onTogglePC: (m: MentorRow) => void;
  togglingPC: string | null;
  onToggleCS: (m: MentorRow) => void;
}

const MentorCard: React.FC<CardProps> = ({ m, idx, showPasscodes, allMentors, onEdit, onPromote, onTogglePC, togglingPC, onToggleCS }) => {
  const days      = daysSince(m.joiningDate || '');
  const bdDays    = birthdayCountdown(m.birthday || '');
  const rbg       = roleBg(m.role);
  const initials  = m.mentorName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const seniorName = m.seniorMentorId ? (allMentors.find(x => x.mentorId === m.seniorMentorId)?.mentorName || m.seniorMentorId) : null;

  /* Birthday tier */
  const bdTier = bdDays === null ? null
    : bdDays === 0 ? 'today'
    : bdDays <= 3  ? 'urgent'
    : bdDays <= 7  ? 'soon'
    : bdDays <= 30 ? 'month'
    : 'far';

  const bdStyle: Record<string, { bg: string; color: string; cls: string; label: string }> = {
    today:  { bg:'linear-gradient(135deg,#fce7f3,#fbcfe8)', color:'#9d174d', cls:'mm-bounce mm-bday-glow', label:'🎂 Birthday Today!' },
    urgent: { bg:'linear-gradient(135deg,#fff7ed,#fed7aa)', color:'#c2410c', cls:'mm-float mm-bday-glow', label:`🔥 ${bdDays}d to birthday!` },
    soon:   { bg:'linear-gradient(135deg,#fff7ed,#fde68a)', color:'#b45309', cls:'mm-float',              label:`🎉 ${bdDays}d to birthday` },
    month:  { bg:'#f5f3ff',  color:'#6d28d9', cls:'',                                                      label:`🎂 ${bdDays}d to birthday` },
    far:    { bg:'#f8fafc',  color:'#94a3b8', cls:'',                                                      label:`🎂 ${bdDays}d` },
  };

  /* Tenure milestone */
  const tenureMilestone = days >= 365 ? '🏆' : days >= 180 ? '🥈' : days >= 90 ? '🥉' : days >= 30 ? '🌟' : '✨';

  return (
    <div className="mm-card bg-white rounded-2xl overflow-hidden border border-gray-100"
      style={{ animationDelay:`${idx * 55}ms`, boxShadow:'0 4px 20px rgba(49,39,65,.07)' }}>

      {/* Role accent top bar */}
      <div className="h-1.5 w-full" style={{ background: roleGradient(m.role) }} />

      <div className="p-5">
        {/* Top row: avatar + badges */}
        <div className="flex items-start justify-between mb-4">
          {/* Avatar with spinning ring */}
          <div className="relative flex-shrink-0">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-base font-black relative z-10"
              style={{ background: roleGradient(m.role), color: '#fff', letterSpacing: '.04em' }}>
              {initials}
            </div>
            {/* Pulsing status dot */}
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white relative mm-pulse-dot"
              style={{ background: '#22c55e' }} />
          </div>

          {/* Role + Placement coord badges */}
          <div className="flex flex-col items-end gap-1.5">
            <span className="text-[10px] font-black px-2.5 py-1 rounded-full border"
              style={{ background: rbg.bg, color: rbg.color, borderColor: rbg.border }}>
              {roleLabel(m.role)}
            </span>
            {m.isPlacementCoordinator && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full mm-shimmer text-amber-900">
                📌 Placement Coord
              </span>
            )}
            {m.isCareerStrategist && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">
                🎯 Career Strategist
              </span>
            )}
          </div>
        </div>

        {/* Name + ID */}
        <div className="mb-3">
          <h3 className="font-black text-gray-900 text-base leading-snug">{m.mentorName}</h3>
          <p className="text-[11px] font-mono text-gray-400 mt-0.5">{m.mentorId}</p>
          {seniorName && (
            <p className="text-[11px] text-gray-400 mt-0.5">↳ Under <span className="font-semibold text-gray-600">{seniorName}</span></p>
          )}
        </div>

        {/* Stats row: tenure + birthday */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {/* Tenure */}
          {m.joiningDate && days >= 0 ? (
            <div className="rounded-xl px-3 py-2.5 text-center"
              style={{ background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)', border:'1px solid #ddd6fe' }}>
              <div className="text-base mb-0.5" style={{ animation: 'mmCountUp .5s ease both' }}>{tenureMilestone}</div>
              <div className="text-sm font-black" style={{ color: '#5b21b6' }}>{tenureLabel(days)}</div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-purple-400 mt-0.5">Working</div>
            </div>
          ) : (
            <div className="rounded-xl px-3 py-2.5 text-center bg-gray-50 border border-gray-100">
              <div className="text-base mb-0.5">📅</div>
              <div className="text-xs text-gray-400 font-medium">No date</div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-gray-300 mt-0.5">Joining</div>
            </div>
          )}

          {/* Birthday */}
          {bdDays !== null ? (
            <div className={`rounded-xl px-3 py-2.5 text-center ${bdStyle[bdTier!].cls}`}
              style={{ background: bdStyle[bdTier!].bg, border:`1px solid ${bdTier === 'today' ? '#f9a8d4' : bdTier === 'urgent' ? '#fed7aa' : bdTier === 'soon' ? '#fde68a' : bdTier === 'month' ? '#ddd6fe' : '#e2e8f0'}` }}>
              <div className={`text-sm font-black leading-snug ${bdTier === 'today' || bdTier === 'urgent' ? 'mm-fire' : ''}`}
                style={{ color: bdStyle[bdTier!].color }}>
                {bdTier === 'today' ? '🎂' : bdTier === 'urgent' ? '🔥' : bdTier === 'soon' ? '🎉' : '🎂'}
              </div>
              <div className="text-xs font-black mt-0.5" style={{ color: bdStyle[bdTier!].color }}>
                {bdDays === 0 ? 'Today!' : `${bdDays}d left`}
              </div>
              <div className="text-[9px] font-bold uppercase tracking-widest mt-0.5"
                style={{ color: bdStyle[bdTier!].color, opacity: .6 }}>Birthday</div>
            </div>
          ) : (
            <div className="rounded-xl px-3 py-2.5 text-center bg-gray-50 border border-gray-100">
              <div className="text-base mb-0.5">🎂</div>
              <div className="text-xs text-gray-400 font-medium">Not set</div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-gray-300 mt-0.5">Birthday</div>
            </div>
          )}
        </div>

        {/* Contact info */}
        <div className="space-y-1.5 mb-4">
          {m.phone && (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50">
              <span className="text-sm flex-shrink-0">📞</span>
              <span className="text-xs font-semibold text-gray-700 truncate">{m.phone}</span>
            </div>
          )}
          {m.email && (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50">
              <span className="text-sm flex-shrink-0">✉️</span>
              <span className="text-xs text-gray-600 truncate" title={m.email}>{m.email}</span>
            </div>
          )}
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50">
            <span className="text-sm flex-shrink-0">🔑</span>
            <span className="text-xs font-mono font-semibold" style={{ color: '#312741' }}>
              {showPasscodes ? m.passcode : '•'.repeat(Math.min(m.passcode.length, 8))}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => onEdit(m)}
            className="flex-1 min-w-0 py-2 rounded-xl text-[11px] font-bold border-2 transition-all hover:scale-[1.02] active:scale-[.97]"
            style={{ borderColor: '#3b82f6', color: '#1d4ed8', background: '#eff6ff' }}>
            ✏️ Edit
          </button>
          <button onClick={() => onPromote(m)}
            className="flex-1 min-w-0 py-2 rounded-xl text-[11px] font-bold border-2 transition-all hover:scale-[1.02] active:scale-[.97]"
            style={{ borderColor: roleAccentColor(m.role), color: rbg.color, background: rbg.bg }}>
            {m.role === 'Senior' ? '⭐ Role' : '🔺 Set Role'}
          </button>
          <button
            onClick={() => onTogglePC(m)}
            disabled={togglingPC === m.mentorId}
            className="w-full py-2 rounded-xl text-[11px] font-bold border-2 transition-all hover:scale-[1.01] active:scale-[.97] disabled:opacity-50"
            style={m.isPlacementCoordinator
              ? { borderColor:'#16a34a', color:'#15803d', background:'#f0fdf4' }
              : { borderColor:'#d1d5db', color:'#6b7280', background:'#f9fafb' }}>
            {togglingPC === m.mentorId ? '⏳ Saving…' : m.isPlacementCoordinator ? '✅ Placement Coord.' : '📌 Set Placement Coord.'}
          </button>
          <button
            onClick={() => onToggleCS(m)}
            disabled={togglingPC === m.mentorId}
            className="w-full py-2 rounded-xl text-[11px] font-bold border-2 transition-all hover:scale-[1.01] active:scale-[.97] disabled:opacity-50"
            style={m.isCareerStrategist
              ? { borderColor:'#0284c7', color:'#075985', background:'#f0f9ff' }
              : { borderColor:'#d1d5db', color:'#6b7280', background:'#f9fafb' }}>
            {m.isCareerStrategist ? '✅ Career Strategist (P2P)' : '🎯 Set Career Strategist'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ──────────────────────────────────────────────────────── */
const ManageMentors: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();

  const [mentors, setMentors]       = useState<MentorRow[]>([]);
  const [loading, setLoading]       = useState(true);
  const [showPasscodes, setShowPasscodes] = useState(false);
  const [togglingPC, setTogglingPC] = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [animKey, setAnimKey]       = useState(0);

  /* Add modal */
  const [showModal, setShowModal]   = useState(false);
  const [newName, setNewName]       = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [newPhone, setNewPhone]     = useState('');
  const [newEmail, setNewEmail]     = useState('');
  const [newJoiningDate, setNewJoiningDate] = useState('');
  const [newBirthday, setNewBirthday] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* Promote modal */
  const [promoteTarget, setPromoteTarget] = useState<MentorRow | null>(null);
  const [promoteRole, setPromoteRole]     = useState<'Senior' | 'Standard' | 'Student Coordinator'>('Standard');
  const [promoteSeniorId, setPromoteSeniorId] = useState('');

  /* Edit modal */
  const [editTarget, setEditTarget] = useState<MentorRow | null>(null);
  const [editEmail, setEditEmail]   = useState('');
  const [editPhone, setEditPhone]   = useState('');
  const [editJoiningDate, setEditJoiningDate] = useState('');
  const [editBirthday, setEditBirthday] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminMentors(adminPassword, true);
      setMentors(res.mentors as MentorRow[]);
      setAnimKey(k => k + 1);
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  /* Only show active mentors */
  const activeMentors = useMemo(() =>
    mentors.filter(m => (m.status || 'Active') === 'Active'), [mentors]);

  /* Sort: by upcoming birthday first (within 30d), then by joining date desc */
  const sorted = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = q
      ? activeMentors.filter(m =>
          m.mentorName.toLowerCase().includes(q) ||
          m.mentorId.toLowerCase().includes(q) ||
          (m.phone || '').includes(q) ||
          (m.email || '').toLowerCase().includes(q))
      : activeMentors;

    return [...filtered].sort((a, b) => {
      const ba = birthdayCountdown(a.birthday || '') ?? 9999;
      const bb = birthdayCountdown(b.birthday || '') ?? 9999;
      const aUrgent = ba <= 30 ? ba : 9999;
      const bUrgent = bb <= 30 ? bb : 9999;
      if (aUrgent !== bUrgent) return aUrgent - bUrgent;
      // Seniors first
      const aS = a.role === 'Senior' ? 0 : a.role === 'Student Coordinator' ? 1 : 2;
      const bS = b.role === 'Senior' ? 0 : b.role === 'Student Coordinator' ? 1 : 2;
      return aS - bS;
    });
  }, [activeMentors, search]);

  /* Upcoming birthday strip (next 7 days) */
  const upcomingBdays = useMemo(() =>
    activeMentors
      .map(m => ({ m, days: birthdayCountdown(m.birthday || '') }))
      .filter(x => x.days !== null && x.days <= 7)
      .sort((a, b) => (a.days ?? 0) - (b.days ?? 0)),
    [activeMentors]);

  /* Stats */
  const seniors = activeMentors.filter(m => m.role === 'Senior').length;
  const coords  = activeMentors.filter(m => m.isPlacementCoordinator).length;

  /* Handlers */
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newPasscode || !newEmail.trim()) { toast.error('Fill required fields'); return; }
    setSubmitting(true);
    try {
      const res = await addMentor(adminPassword, newName, newPasscode, newPhone);
      if (newEmail || newJoiningDate || newBirthday) {
        await updateMentorDetails(adminPassword, res.mentorId, { email: newEmail, joiningDate: newJoiningDate, birthday: newBirthday });
      }
      toast.success(`Mentor added: ${res.mentorId}`);
      setShowModal(false);
      setNewName(''); setNewPasscode(''); setNewPhone(''); setNewEmail(''); setNewJoiningDate(''); setNewBirthday('');
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const openPromote = (m: MentorRow) => {
    setPromoteTarget(m);
    setPromoteRole((m.role === 'Senior' ? 'Senior' : m.role === 'Student Coordinator' ? 'Student Coordinator' : 'Standard') as any);
    setPromoteSeniorId(m.seniorMentorId || '');
  };

  const openEdit = (m: MentorRow) => {
    setEditTarget(m);
    setEditEmail(m.email || '');
    setEditPhone(m.phone || '');
    setEditJoiningDate(m.joiningDate || '');
    setEditBirthday(m.birthday || '');
  };

  const handlePromote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoteTarget) return;
    setSubmitting(true);
    try {
      await promoteMentor(adminPassword, promoteTarget.mentorId, promoteRole, promoteRole === 'Standard' ? promoteSeniorId : '');
      toast.success(`${promoteTarget.mentorName} → ${promoteRole}`);
      setPromoteTarget(null);
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await updateMentorDetails(adminPassword, editTarget.mentorId, { email: editEmail, phone: editPhone, joiningDate: editJoiningDate, birthday: editBirthday });
      toast.success('Details updated');
      setEditTarget(null);
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setEditSaving(false); }
  };

  const handleTogglePC = async (m: MentorRow) => {
    setTogglingPC(m.mentorId);
    try {
      const next = !m.isPlacementCoordinator;
      await setPlacementCoordinator(adminPassword, m.mentorId, next);
      toast.success(next ? `${m.mentorName} is now Placement Coordinator` : `${m.mentorName} removed from Placement Coordinator`);
      setMentors(prev => prev.map(x => x.mentorId === m.mentorId ? { ...x, isPlacementCoordinator: next } : x));
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    finally { setTogglingPC(null); }
  };

  // A Career Strategist: signs in on the mentor login and runs the P2P students
  const handleToggleCS = async (m: MentorRow) => {
    setTogglingPC(m.mentorId);
    try {
      const next = !m.isCareerStrategist;
      await setCareerStrategist(adminPassword, m.mentorId, next);
      toast.success(next ? `${m.mentorName} is now a Career Strategist: they see the P2P students after signing in again` : `${m.mentorName} is no longer a Career Strategist`);
      setMentors(prev => prev.map(x => x.mentorId === m.mentorId ? { ...x, isCareerStrategist: next } : x));
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    finally { setTogglingPC(null); }
  };

  const seniorsList = activeMentors.filter(m => m.role === 'Senior');

  return (
    <TrackerLayout>
      <style>{ANIM_CSS}</style>
      <div className="space-y-5 pb-8">

        {/* ── Hero Header ── */}
        <div className="relative overflow-hidden rounded-2xl p-6"
          style={{ background:'linear-gradient(135deg,#1e1433 0%,#312741 50%,#4a2060 100%)' }}>
          {/* Decorative orbs */}
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
            style={{ background:'radial-gradient(circle,#ffe500,transparent)', transform:'translate(30%,-30%)' }} />
          <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-10"
            style={{ background:'radial-gradient(circle,#a78bfa,transparent)', transform:'translate(-30%,30%)' }} />

          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-black text-white tracking-tight">Mentor Team</h1>
              </div>
              <div className="flex flex-wrap gap-3 mt-2">
                {[
                  { label: `${activeMentors.length} Active`, icon: '👥', color: '#a78bfa' },
                  { label: `${seniors} Senior${seniors !== 1 ? 's' : ''}`, icon: '⭐', color: '#fbbf24' },
                  { label: `${coords} Placement`, icon: '📌', color: '#34d399' },
                ].map(s => (
                  <div key={s.label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                    style={{ background:'rgba(255,255,255,.08)', backdropFilter:'blur(4px)' }}>
                    <span className="text-sm">{s.icon}</span>
                    <span className="text-xs font-bold" style={{ color: s.color }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => setShowPasscodes(!showPasscodes)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-all hover:scale-105"
                style={{ borderColor:'rgba(255,255,255,.25)', color:'rgba(255,255,255,.8)', background:'rgba(255,255,255,.08)' }}>
                {showPasscodes ? '🙈 Hide' : '👁 Show'} Passcodes
              </button>
              <button onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black transition-all hover:scale-105 active:scale-[.97]"
                style={{ background:'linear-gradient(135deg,#ffe500,#ffa500)', color:'#312741', boxShadow:'0 4px 16px rgba(255,165,0,.4)' }}>
                ＋ Add Mentor
              </button>
            </div>
          </div>
        </div>

        {/* ── Birthday Alert Strip ── */}
        {upcomingBdays.length > 0 && (
          <div className="mm-banner rounded-2xl overflow-hidden"
            style={{ background:'linear-gradient(135deg,#fce7f3,#f3e8ff)', border:'1px solid #f9a8d4' }}>
            <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
              <span className="text-lg mm-bounce flex-shrink-0">🎂</span>
              <p className="text-xs font-black text-pink-800 uppercase tracking-widest flex-shrink-0">Upcoming Birthdays</p>
              <div className="flex gap-2 flex-wrap">
                {upcomingBdays.map(({ m, days }) => (
                  <span key={m.mentorId}
                    className={`text-[11px] font-bold px-3 py-1 rounded-full ${days === 0 ? 'mm-bday-glow' : ''}`}
                    style={{ background: days === 0 ? '#fbcfe8' : '#f3e8ff', color: days === 0 ? '#9d174d' : '#6d28d9', border:`1px solid ${days === 0 ? '#f9a8d4' : '#ddd6fe'}` }}>
                    {days === 0 ? `🎉 ${m.mentorName} — TODAY!` : `${m.mentorName} — ${days}d`}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Search bar ── */}
        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm pointer-events-none">🔍</span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search mentors by name, ID, phone…"
            className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-200 text-sm outline-none focus:border-purple-400 bg-white transition-all"
          />
          {search && (
            <button onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg leading-none">×</button>
          )}
        </div>

        {/* ── Cards ── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-gray-100 overflow-hidden" style={{ height: 280, animation:`mmShimmer 1.5s ease infinite`, animationDelay:`${i*100}ms` }}>
                <div className="h-1.5 w-full bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="flex gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-gray-200" />
                    <div className="flex-1 space-y-2 pt-1">
                      <div className="h-3 bg-gray-200 rounded-full w-3/4" />
                      <div className="h-2 bg-gray-200 rounded-full w-1/2" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-16 bg-gray-200 rounded-xl" />
                    <div className="h-16 bg-gray-200 rounded-xl" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-5xl mb-4">🔍</p>
            <p className="font-bold text-gray-600">{search ? 'No mentors match your search' : 'No active mentors yet'}</p>
            <p className="text-gray-400 text-sm mt-1">{search ? 'Try a different name or ID' : 'Add the first mentor to get started'}</p>
          </div>
        ) : (
          <div key={animKey} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sorted.map((m, idx) => (
              <MentorCard key={m.mentorId} m={m} idx={idx}
                showPasscodes={showPasscodes} allMentors={mentors}
                onEdit={openEdit} onPromote={openPromote}
                onTogglePC={handleTogglePC} togglingPC={togglingPC} onToggleCS={handleToggleCS} />
            ))}
          </div>
        )}

        {/* Footer note */}
        {!loading && activeMentors.length < mentors.length && (
          <p className="text-xs text-gray-400 text-center">
            {mentors.length - activeMentors.length} inactive mentor{mentors.length - activeMentors.length !== 1 ? 's' : ''} hidden
          </p>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-800">
          <strong>Note:</strong> Passcodes are stored in the Google Sheet. Only admins can view them here. Share each passcode privately with the mentor.
        </div>
      </div>

      {/* ── Add Mentor Modal ── */}
      {showModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10 max-h-[92vh] overflow-y-auto mm-pop">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-base font-black" style={{ color:'#312741' }}>Add Mentor</h3>
                <p className="text-xs text-gray-400 mt-0.5">Create a new mentor account</p>
              </div>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-lg leading-none transition-colors">×</button>
            </div>
            <form onSubmit={handleAdd} className="space-y-4">
              {[
                { label:'Full Name *', val:newName, set:setNewName, ph:'Mentor full name', req:true },
                { label:'Phone *', val:newPhone, set:setNewPhone, ph:'+91 98944 47857', req:true, hint:'Shown to students in welcome email.' },
                { label:'Email *', val:newEmail, set:setNewEmail, ph:'mentor@email.com', req:true, hint:'They sign in to the app with this email and the passcode.' },
                { label:'Passcode *', val:newPasscode, set:setNewPasscode, ph:'Set a passcode', req:true, hint:'Their login password — share it privately with the mentor.' },
              ].map(f => (
                <div key={f.label}>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">{f.label}</label>
                  <input value={f.val} onChange={e => f.set(e.target.value)} required={f.req} placeholder={f.ph}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400 transition-colors" />
                  {(f as any).hint && <p className="text-xs text-gray-400 mt-1">{(f as any).hint}</p>}
                </div>
              ))}
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Joining Date</label>
                <input type="date" value={newJoiningDate} onChange={e => setNewJoiningDate(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Birthday (MM-DD)</label>
                <input type="text" value={newBirthday} onChange={e => setNewBirthday(e.target.value)} placeholder="07-15 (July 15)"
                  pattern="\d{2}-\d{2}"
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
                <p className="text-xs text-gray-400 mt-1">Format: MM-DD</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-60 transition-all hover:scale-[1.02]"
                  style={{ background:'linear-gradient(135deg,#312741,#6d28d9)' }}>
                  {submitting ? 'Adding…' : 'Add Mentor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Details Modal ── */}
      {editTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={() => setEditTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10 mm-pop">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-base font-black" style={{ color:'#312741' }}>Edit Mentor</h3>
                <p className="text-xs text-gray-400 mt-0.5 font-semibold">{editTarget.mentorName}</p>
              </div>
              <button onClick={() => setEditTarget(null)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-lg leading-none">×</button>
            </div>
            <form onSubmit={handleEditSave} className="space-y-4">
              {[
                { label:'Phone', val:editPhone, set:setEditPhone, ph:'+91 98944 47857' },
                { label:'Email', val:editEmail, set:setEditEmail, ph:'mentor@email.com' },
              ].map(f => (
                <div key={f.label}>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">{f.label}</label>
                  <input value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
                </div>
              ))}
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Joining Date</label>
                <input type="date" value={editJoiningDate} onChange={e => setEditJoiningDate(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
                {editJoiningDate && (
                  <p className="text-xs text-purple-600 mt-1 font-semibold">🗓 {daysSince(editJoiningDate)}d working · {tenureLabel(daysSince(editJoiningDate))}</p>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Birthday (MM-DD)</label>
                <input type="text" value={editBirthday} onChange={e => setEditBirthday(e.target.value)} placeholder="07-15"
                  pattern="\d{2}-\d{2}"
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
                {editBirthday && birthdayCountdown(editBirthday) !== null && (
                  <p className="text-xs text-purple-600 mt-1 font-semibold">
                    🎂 {birthdayCountdown(editBirthday) === 0 ? 'Birthday is today! 🎉' : `${birthdayCountdown(editBirthday)}d until birthday`}
                  </p>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditTarget(null)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={editSaving}
                  className="flex-1 py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-60 transition-all hover:scale-[1.02]"
                  style={{ background:'linear-gradient(135deg,#312741,#6d28d9)' }}>
                  {editSaving ? 'Saving…' : 'Save Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Role Modal ── */}
      {promoteTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={() => setPromoteTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10 mm-pop">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-base font-black" style={{ color:'#312741' }}>Set Role</h3>
                <p className="text-xs text-gray-400 mt-0.5 font-semibold">{promoteTarget.mentorName}</p>
              </div>
              <button onClick={() => setPromoteTarget(null)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-lg leading-none">×</button>
            </div>
            <form onSubmit={handlePromote} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Role</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['Standard', 'Senior', 'Student Coordinator'] as const).map(r => (
                    <button key={r} type="button" onClick={() => setPromoteRole(r)}
                      className="py-2.5 rounded-xl text-[11px] font-bold border-2 transition-all"
                      style={promoteRole === r
                        ? { background:'#312741', color:'#ffe500', borderColor:'#312741' }
                        : { background:'#fff', color:'#6b7280', borderColor:'#e5e7eb' }}>
                      {r === 'Standard' ? '👨‍💻 Standard' : r === 'Senior' ? '⭐ Senior' : '🎓 Coord.'}
                    </button>
                  ))}
                </div>
              </div>

              {promoteRole === 'Standard' && seniorsList.length > 0 && (
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Assign Under Senior (optional)</label>
                  <select value={promoteSeniorId} onChange={e => setPromoteSeniorId(e.target.value)}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400 bg-white">
                    <option value="">— None (independent) —</option>
                    {seniorsList.filter(s => s.mentorId !== promoteTarget.mentorId).map(s => (
                      <option key={s.mentorId} value={s.mentorId}>{s.mentorName}</option>
                    ))}
                  </select>
                </div>
              )}

              {promoteRole === 'Senior' && (
                <div className="rounded-xl p-3 text-xs text-amber-800" style={{ background:'#fffbeb', border:'1px solid #fde68a' }}>
                  ⭐ This mentor will see the Team Overview tab with all junior mentors and their student activity.
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setPromoteTarget(null)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-60 transition-all hover:scale-[1.02]"
                  style={{ background:'linear-gradient(135deg,#312741,#6d28d9)' }}>
                  {submitting ? 'Saving…' : 'Save Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </TrackerLayout>
  );
};

export default ManageMentors;
